import { Delete } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../app/hooks'
import {
  useDeleteVerifiedDomainMutation,
  useGetVerifiedDomainsQuery,
  useVerifyDomainMutation,
} from '../../../app/redux-fetch/apiQuery'
import AddButton from '../../../components/Buttons/AddButton'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../components/Typographies/TableTitleTypography'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import CreateVerifiedDomainModal from './CreateVerifiedDomainModal'
import StatusChip from './StatusChip'
import messages from './messages'
import { VerifiedDomainDto, VerifiedDomainResultCode, VerifiedDomainStatus } from './types'

const VERIFY_WINDOW_SECONDS = 60

const VerifiedDomainsTable = () => {
  const { formatMessage } = useIntl()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const { tenantId, customerId } = loggedInUser

  const { data: domains, isSuccess } = useGetVerifiedDomainsQuery(
    { tenantId, customerId },
    { refetchOnMountOrArgChange: true, skip: !tenantId },
  )
  const [verifyDomain] = useVerifyDomainMutation()
  const [deleteVerifiedDomain] = useDeleteVerifiedDomainMutation()

  const [createOpen, setCreateOpen] = useState(false)
  const [toRemove, setToRemove] = useState<VerifiedDomainDto | undefined>()
  // Per-row verify cooldown (seconds remaining) after a 429 from the rate limiter.
  const [cooldowns, setCooldowns] = useState<Record<number, number>>({})
  // The row whose verify is in flight - guards against a double-click firing two /verify calls
  // before the first resolves (the optimistic window, before any 429).
  const [verifyingId, setVerifyingId] = useState<number | undefined>()

  useEffect(() => {
    if (Object.keys(cooldowns).length === 0) return
    const timer = setInterval(() => {
      setCooldowns((prev) => {
        const next: Record<number, number> = {}
        for (const [id, secs] of Object.entries(prev)) {
          if (secs > 1) next[Number(id)] = secs - 1
        }
        return next
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [cooldowns])

  const handleVerify = async (id: number) => {
    setVerifyingId(id)
    try {
      const res = await verifyDomain({ tenantId, id })
      if ('error' in res && res.error) {
        if ((res.error as { status?: number }).status === 429) {
          // Rate limited - disable the row's Verify button. Use the server's Retry-After when it
          // came through, else the documented 60s window. (The baseQuery already toasts the error.)
          const retryAfter = (res.error as { retryAfter?: number }).retryAfter
          setCooldowns((prev) => ({ ...prev, [id]: retryAfter && retryAfter > 0 ? retryAfter : VERIFY_WINDOW_SECONDS }))
        }
        return
      }
      const data = 'data' in res ? res.data : undefined
      if (data?.resultCode === VerifiedDomainResultCode.Verified) {
        enqueueSnackbar(formatMessage(messages.verifyVerified), { variant: 'success' })
      } else if (data?.resultCode === VerifiedDomainResultCode.Failed) {
        // Surface the per-resolver disagreement detail verbatim when the server supplied it.
        enqueueSnackbar(
          data.failureReason
            ? `${formatMessage(messages.verifyFailed)} ${data.failureReason}`
            : formatMessage(messages.verifyFailed),
          { variant: 'warning' },
        )
      } else {
        enqueueSnackbar(formatMessage(messages.verifyStale), { variant: 'info' })
      }
    } finally {
      setVerifyingId(undefined)
    }
  }

  const handleRemove = async () => {
    if (!toRemove) return
    const res = await deleteVerifiedDomain({ tenantId, id: toRemove.id })
    if ('error' in res && res.error) return // the baseQuery already surfaces the error toast
    enqueueSnackbar(formatMessage(messages.domainRemoved), { variant: 'success' })
    setToRemove(undefined)
  }

  const columns = useMemo(
    () => [
      { accessorKey: 'domainName', header: formatMessage(messages.domainName) },
      {
        accessorKey: 'status',
        header: formatMessage(messages.status),
        Cell: ({ row }: { row: { original: VerifiedDomainDto } }) => (
          <StatusChip status={row.original.status} />
        ),
      },
      {
        accessorFn: (row: VerifiedDomainDto) =>
          row.verifiedAt ? dayjs(row.verifiedAt).format('YYYY-MM-DD HH:mm') : '—',
        header: formatMessage(messages.verifiedAt),
      },
    ],
    [formatMessage],
  )

  return (
    <>
      <SideModalWrapper
        open={createOpen}
        key='create'
        headerLabel={formatMessage(messages.addDomain)}
        onClose={() => setCreateOpen(false)}
        withBottomBox={false}
      >
        <CreateVerifiedDomainModal onDone={() => setCreateOpen(false)} />
      </SideModalWrapper>

      <Dialog open={Boolean(toRemove)} onClose={() => setToRemove(undefined)}>
        <DialogTitle>{formatMessage(messages.confirmRemoveTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {formatMessage(messages.confirmRemoveBody, { domain: toRemove?.domainName ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRemove(undefined)}>{formatMessage(messages.cancel)}</Button>
          <Button color='error' variant='contained' onClick={handleRemove}>
            {formatMessage(messages.remove)}
          </Button>
        </DialogActions>
      </Dialog>

      <MRTDataGridv2
        state={{ isLoading: !isSuccess }}
        initialState={{ sorting: [{ id: 'domainName', desc: false }] }}
        leftHeadingComponent={
          <Box sx={{ display: 'inline-flex' }}>
            <TableTitleTypography>{formatMessage(messages.title)}</TableTitleTypography>
          </Box>
        }
        rightHeadingComponent={
          <AddButton text={formatMessage(messages.addDomain)} onClick={() => setCreateOpen(true)} />
        }
        columns={columns}
        data={domains || []}
        getRowId={(row) => String(row.id)}
        enableRowActions
        positionActionsColumn='last'
        renderDetailPanel={({ row }: { row: { original: VerifiedDomainDto } }) =>
          row.original.status === VerifiedDomainStatus.Failed && row.original.verificationFailureReason ? (
            <Box sx={{ p: 1 }}>
              <Typography variant='body2' color='error'>
                {/* Plain text - React escapes it, so the server reason is XSS-safe. */}
                {row.original.verificationFailureReason}
              </Typography>
            </Box>
          ) : null
        }
        renderRowActions={({ row }: { row: { original: VerifiedDomainDto } }) => {
          const domain = row.original
          const cooldown = cooldowns[domain.id] ?? 0
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {domain.status !== VerifiedDomainStatus.Verified && (
                <Button
                  size='small'
                  variant='outlined'
                  disabled={cooldown > 0 || verifyingId === domain.id}
                  onClick={() => handleVerify(domain.id)}
                >
                  {cooldown > 0
                    ? formatMessage(messages.verifyRetryIn, { seconds: cooldown })
                    : formatMessage(messages.verifyNow)}
                </Button>
              )}
              <IconButton
                color='primary'
                aria-label={formatMessage(messages.remove)}
                onClick={() => setToRemove(domain)}
              >
                <Delete />
              </IconButton>
            </Box>
          )
        }}
      />
    </>
  )
}

export default VerifiedDomainsTable
